import { useMemo, useState, useCallback } from 'react';
import { Button, capitalize, TextInput, Tooltip } from '@datassential/platform-ui-lib';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { IoIosSearch } from 'react-icons/io';
import { getInstantChartUrl } from '@routes';
import Page from '@components/Page/Page';
import { useDebouncedCallback } from '@hooks/useDebouncedCallback';
import useDownload from '@apiHooks/useDownload';
import { currentCountrySelector } from '@selectors/countries.selector';
import { highlightMatches } from '@core/helpers';
import { useInstantChartsData, SubCategory, InstantChart } from './useInstantChartsData';
import { useSearchMatches } from './useSearchMatches';
import KeywordsList from './KeywordsList';
import {
    InstantChartsColumn,
    InstantChartsColumnBody,
    InstantChartsColumnFooter,
    InstantChartsColumnRow
} from './InstantChartsColumn';
import './InstantCharts.styles.scss';

const BIG_RESULTS_COUNT = 10000;

const InstantCharts = () => {
    const navigate = useNavigate();
    const countryCode = useSelector(currentCountrySelector);

    const [selectedSubCategory, setSelectedSubCategory] = useState<SubCategory | null>(null);
    const [selectedChart, setSelectedChart] = useState<InstantChart | null>(null);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const debouncedSetSearch = useDebouncedCallback((value: string) => {
        setDebouncedSearch(value);
    }, 300);

    const { subCategories } = useInstantChartsData();
    const {
        subCategoryMatches,
        chartMatches,
        subCategoriesNameMatches,
        chartsNameMatches,
        subCategoriesNameStarts,
        chartsNameStarts,
    } = useSearchMatches(debouncedSearch, subCategories);
    const downloadChartReport = useDownload('download instant chart report');

    const preSortedSubCategories = useMemo(
        () => [...subCategories].sort(
            (a, b) => (a.name ?? '').toLocaleLowerCase() < (b.name ?? '').toLocaleLowerCase() ? -1 : 1,
        ),
        [subCategories]
    );

    const sortedSubCategories = useMemo(() => {
        if (subCategoryMatches.size === 0) return preSortedSubCategories;
        return [...preSortedSubCategories].sort((a, b) => {
            let aResultsCount = Number(subCategoryMatches.get(a.name ?? '') ?? -1);
            if (subCategoriesNameMatches.includes(a.name ?? '')) aResultsCount += BIG_RESULTS_COUNT;
            if (subCategoriesNameStarts.includes(a.name ?? '')) aResultsCount += BIG_RESULTS_COUNT;

            let bResultsCount = Number(subCategoryMatches.get(b.name ?? '') ?? -1);
            if (subCategoriesNameMatches.includes(b.name ?? '')) bResultsCount += BIG_RESULTS_COUNT;
            if (subCategoriesNameStarts.includes(b.name ?? '')) bResultsCount += BIG_RESULTS_COUNT;

            return bResultsCount - aResultsCount;
        });
    }, [preSortedSubCategories, subCategoryMatches, subCategoriesNameMatches, subCategoriesNameStarts]);

    const sortedCharts = useMemo(() => {
        const charts = [...(selectedSubCategory?.charts ?? [])];
        charts.sort((a, b) => (a.name ?? '').toLocaleLowerCase() < (b.name ?? '').toLocaleLowerCase() ? -1 : 1);

        if (chartMatches.size === 0) return charts;
        return [...charts].sort((a, b) => {
            let aResultsCount = Number(chartMatches.get(String(a.id))?.size ?? -1);
            if (chartsNameMatches.includes(a.name ?? '')) aResultsCount += BIG_RESULTS_COUNT;
            if (chartsNameStarts.includes(a.name ?? '')) aResultsCount += BIG_RESULTS_COUNT;

            let bResultsCount = Number(chartMatches.get(String(b.id))?.size ?? -1);
            if (chartsNameMatches.includes(b.name ?? '')) bResultsCount += BIG_RESULTS_COUNT;
            if (chartsNameStarts.includes(b.name ?? '')) bResultsCount += BIG_RESULTS_COUNT;

            return bResultsCount - aResultsCount;
        });
    }, [selectedSubCategory, chartMatches, chartsNameMatches, chartsNameStarts]);

    const hasNoMatches = !!debouncedSearch && subCategoryMatches.size === 0;

    const handleSearchChange = useCallback((value: string) => {
        setSearch(value);
        debouncedSetSearch(value);
        if (value) {
            setSelectedSubCategory(null);
            setSelectedChart(null);
        }
    }, [debouncedSetSearch]);

    const handleSubCategoryClick = useCallback((sub) => {
        setSelectedSubCategory(sub);
        setSelectedChart(null);
    }, []);

    const handleViewClick = useCallback(() => {
        if (!selectedChart) return;

        navigate(getInstantChartUrl(selectedChart.id, selectedChart.wordsCount ?? 0));
    }, [selectedChart, navigate]);

    const handleDownloadClick = useCallback(() => {
        if (!selectedChart) return;

        downloadChartReport({ instantChartId: selectedChart.id, countryCode });
    }, [downloadChartReport, selectedChart, countryCode]);

    return (
        <Page isWrapped title="Instant Charts">
            <div className="InstantCharts">
                <div className="InstantCharts__title">Prebuilt instant charts</div>
                <p className="InstantCharts__search-label">Search for an Instant Chart</p>
                <div
                    className="InstantCharts__search-wrapper appears-top del-1"
                    data-tooltip-id="instantCharts-search-tooltip"
                >
                    <TextInput
                        className="InstantCharts__search"
                        value={search}
                        setValue={handleSearchChange}
                        placeholder="Search foods and flavors"
                        testId="inputInstantChartsSearch"
                    >
                        <IoIosSearch className="InstantCharts__search-icon"/>
                    </TextInput>
                    <Tooltip
                        id="instantCharts-search-tooltip"
                        place="right"
                        isOpen={hasNoMatches}
                    >
                        No results found
                    </Tooltip>
                </div>
                <div className="InstantCharts__columns  appears-top del-2">
                        <InstantChartsColumn
                            testId="instantChartsSubCategories"
                            title="CATEGORY NAME"
                        >
                            <InstantChartsColumnBody key={debouncedSearch}>
                                {sortedSubCategories.map((sub) => {
                                    const subName = sub.name ?? '';
                                    const label = highlightMatches(capitalize(subName), debouncedSearch);
                                    return (
                                        <InstantChartsColumnRow
                                            key={sub.name}
                                            label={label}
                                            isSelected={selectedSubCategory?.name === sub.name}
                                            isMatched={subCategoryMatches.has(subName)}
                                            matchCount={subCategoryMatches.get(subName) ?? 0}
                                            onClick={() => handleSubCategoryClick(sub)}
                                            testId={`instantChartsSubCategory-${sub.name}`}
                                        />
                                    );
                                })}
                            </InstantChartsColumnBody>
                        </InstantChartsColumn>

                        <InstantChartsColumn
                            testId="instantChartsCharts"
                            title="CHART NAME"
                            noResults={!selectedSubCategory}
                        >
                            <InstantChartsColumnBody key={selectedSubCategory?.name}>
                                {sortedCharts.map((chart, index) => {
                                    const chartId = String(chart.id);
                                    const label = highlightMatches(capitalize(chart.name ?? ''), debouncedSearch);

                                    return (
                                        <InstantChartsColumnRow
                                            key={chart.id}
                                            className={`appears-left del-${index + 1}`}
                                            label={label}
                                            isSelected={selectedChart?.id === chart.id}
                                            isMatched={chartMatches.has(chartId)}
                                            matchCount={chartMatches.get(chartId)?.size ?? 0}
                                            onClick={() => setSelectedChart(chart)}
                                            testId={`instantChartsChart-${chart.id}`}
                                        />
                                    );
                                })}
                            </InstantChartsColumnBody>
                        </InstantChartsColumn>

                        <InstantChartsColumn
                            testId="instantChartsKeywords"
                            title="CHART DETAILS"
                            noResults={!selectedSubCategory || !selectedChart}
                        >
                            {selectedSubCategory && selectedChart && (
                                <>
                                    <InstantChartsColumnBody key={selectedChart.id}>
                                        <KeywordsList
                                            subCategory={selectedSubCategory}
                                            chart={selectedChart}
                                            matchedKeywords={chartMatches.get(String(selectedChart.id))}
                                            searchValue={debouncedSearch}
                                        />
                                    </InstantChartsColumnBody>
                                    <InstantChartsColumnFooter>
                                        <Button className="InstantCharts__view-btn" onClick={handleDownloadClick}>
                                            Download
                                        </Button>
                                        <Button className="InstantCharts__view-btn" onClick={handleViewClick}>
                                            View
                                        </Button>
                                    </InstantChartsColumnFooter>
                                </>
                            )}
                        </InstantChartsColumn>
                    </div>
            </div>
        </Page>
    );
};

export default InstantCharts;
